/**
 * Tenant discipline options for the programme editor.
 *
 * The programme identity step offers a discipline picker whose options are
 * tenant-specific: a tenant customises them through its provider settings. The
 * step renders deep in the editor tree and is unit-tested in isolation, so it
 * reads the options from this context rather than fetching them itself. The
 * shell resolves the tenant's disciplines from the platform API and provides
 * them; outside a provider (standalone tests, or a tenant that has never
 * customised the list) {@link useTenantDisciplines} falls back to
 * {@link DEFAULT_DISCIPLINES}.
 *
 * @module components/TenantDisciplinesContext
 */

import React, { createContext, useContext } from "react";

/**
 * The institution-neutral discipline set used when a tenant has not customised
 * its own. Kept broad so a fresh tenant still gets a usable picker.
 */
export const DEFAULT_DISCIPLINES: readonly string[] = [
  "Computing",
  "Business",
  "Psychology",
  "Education",
  "Engineering",
  "Science",
  "Health & Social Care",
  "Arts & Humanities",
  "Law",
  "Energy & Built Environment",
  "Tourism & Hospitality",
  "Culinary Arts",
  "Personal Services",
  "Other",
];

const TenantDisciplinesContext = createContext<readonly string[] | null>(null);

export interface TenantDisciplinesProviderProps {
  /** The tenant's disciplines; an empty list is treated as "use the defaults". */
  disciplines: readonly string[];
  children: React.ReactNode;
}

/**
 * Provide the tenant's discipline options to the editor tree. An empty list
 * resolves to {@link DEFAULT_DISCIPLINES}, so a tenant with no custom list still
 * presents a full picker.
 */
export function TenantDisciplinesProvider({
  disciplines,
  children,
}: TenantDisciplinesProviderProps) {
  return (
    <TenantDisciplinesContext.Provider
      value={disciplines.length > 0 ? disciplines : DEFAULT_DISCIPLINES}
    >
      {children}
    </TenantDisciplinesContext.Provider>
  );
}

/**
 * Read the tenant's discipline options, falling back to
 * {@link DEFAULT_DISCIPLINES} when no provider is present.
 */
export function useTenantDisciplines(): readonly string[] {
  return useContext(TenantDisciplinesContext) ?? DEFAULT_DISCIPLINES;
}
